import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { useI18n } from '../utilities/i18n';
import '../styles/createMatchModal.css';

function EditStageModal({ isOpen, onClose, onSuccess, stage, league, season }) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  
  const [stageName, setStageName] = useState('');
  const [stageShortName, setStageShortName] = useState('');
  const [priority, setPriority] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (stage) {
      setStageName(stage.name || '');
      setStageShortName(stage.short_name || '');
      setPriority(stage.priority || '');
    }
  }, [stage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stageName.trim()) {
      setMessage({ text: t('stNameRequired'), type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const stageData = {
        name: stageName.trim(),
        short_name: stageShortName.trim() || null,
        priority: priority || null,
        group_id: stage.group_id || null,
        league_id: league.value,
        season_id: season.value,
        stages_amount: 1,
        matches: []
      };

      const response = await axios.post(
        BASE_URL.url + `/leagues/changeStage/${stage.id}`,
        stageData,
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      setMessage({ text: t('stUpdated'), type: 'success' });
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating stage:', error);
      setMessage({ 
        text: error.response?.data?.message || t('stUpdateError'), 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !stage) return null;

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content create-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('stEditTitle')}</h2>
          <button className="modal-close" onClick={onClose} type="button">
            <FiX size={24} />
          </button>
        </div>
      
        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-section-modal">
              <label className="form-label-modal form-label-required">
                {t('stName')}
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                placeholder={t('stNamePh')}
                maxLength={100}
              />
            </div>

            <div className="form-section-modal">
              <label className="form-label-modal">
                {t('stShortName')}
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={stageShortName}
                onChange={(e) => setStageShortName(e.target.value)}
                placeholder={t('stShortPh')}
                maxLength={50}
              />
            </div>

            <div className="form-section-modal">
              <label className="form-label-modal">
                {t('stPriority')}
              </label>
              <input
                className="input-field-modal"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder={t('stPriorityPh')}
                min="1"
              />
            </div>

            <div className="button-group-modal">
              <button
                type="submit"
                disabled={loading}
                className="btn-modal btn-primary-modal"
              >
                {loading ? t('stSaving') : t('stSaveChanges')}
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditStageModal;
